import { Button, Col, Form, Input, Radio, RadioChangeEvent, Row, Spin } from 'antd';
import { useMutation, useQuery } from 'react-query';
import { useTranslation, Trans } from 'react-i18next';
import classNames from 'classnames';
import { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { getCompanyDetails, rebuyCheckAvalableSlot, rebuySession } from '@/common/services';
import {
  capitalizeFirstLetter,
  formatDate,
  formatTimeRange,
  showNotification,
  BookingSessionViewModel,
} from '@/common/utils';
import { useAuth } from '@/hooks';
import { ModalContent, ModalDialog, ModalHeader } from '../modal';
import { ConfirmedIcon } from '../icons';
import { DateTimePicker } from '../datetime-picker';
import { CenteredSpinner } from '../centered-spinner';
import styles from './styles.module.scss';

type Props = {
  session: BookingSessionViewModel;
  onCancel: VoidFunction;
};

enum PURPOSE {
  FOLLOW_UP_PREVIOUS_SESSION,
  OTHER,
}

type RebuyFormValues = {
  purpose: PURPOSE;
  bookingDateAndTime: Dayjs;
  otherPurpose: string;
  other: string;
};

export function RebuyModal(props: Props) {
  const { isAdvisor, user } = useAuth();
  const [showOtherPurpose, setShowOtherPurpose] = useState(false);
  const { session, onCancel } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const getCompanyInfoQuery = useQuery([], () => {
    if (!session.companyId) {
      return Promise.resolve(null);
    }
    return getCompanyDetails(session.companyId);
  });

  const validateTimeQuery = useQuery(
    ['validateTimeQuery'],
    () => {
      const bookingTime = form.getFieldValue('bookingDateAndTime') as Dayjs;

      return rebuyCheckAvalableSlot({
        currentUserId: user?.id!,
        previousBookingId: session.id,
        bookingTime: bookingTime.toISOString(),
      });
    },
    { enabled: false }
  );

  const submitAction = useMutation(
    (values: RebuyFormValues) => {
      const params: API.RebuySessionParams = {
        locale: session.locale,
        currentUserId: user?.id!!,
        previousBookingId: session.id,
        advisorEmail: session.advisorEmail,
        advisorFullName: session.advisorName,
        customerEmail: session.customerEmail,
        customerFullName: session.customerFullName,
        customerCompany: session.customerCompanyName,
        bookingTime: values.bookingDateAndTime.toISOString(),
        purpose:
          values.purpose === PURPOSE.FOLLOW_UP_PREVIOUS_SESSION
            ? t('rebuy.bookingForm.purposeFollowUpOption')
            : values.otherPurpose,
        other: values.other,
      };
      return rebuySession(session.id, params);
    },
    {
      onSuccess() {
        showNotification({ type: 'success', message: t('rebuy.bookingForm.successMessage') });
        onCancel();
      },
    }
  );

  const onPurposeChange = useCallback((e: RadioChangeEvent) => {
    setShowOtherPurpose(e.target.value === PURPOSE.OTHER);
  }, []);

  if (!isAdvisor) {
    return;
  }

  return (
    <ModalDialog>
      <ModalHeader onCancel={onCancel}>
        <div className={styles.infoRow}>
          <div className={styles.iconWrapper}>
            <ConfirmedIcon size="large" />
          </div>
          <div className={styles.textWrapper}>
            <div className={classNames(styles.mainText)}>{t('rebuy.bookingForm.title')}</div>
            <div className={styles.subText}>
              {t('rebuy.bookingForm.previousBookingId', { bookingId: session.id })}
            </div>
          </div>
        </div>
        <div className={styles.titleRow}>
          <div className={styles.block}>
            <div className={styles.label}>{t('home.date')}:</div>
            <div>{capitalizeFirstLetter(formatDate(session.startTime, 'dddd DD MMM'))}</div>
          </div>
          <div className={styles.block}>
            <div className={styles.label}>{t('home.time')}:</div>
            <div>{formatTimeRange(session.startTime, session.endTime)}</div>
          </div>
          {isAdvisor && (
            <div className={styles.block}>
              <div className={styles.label}>{t('home.customer')}:</div>
              <div>
                {session.customerFullName}
                {session.customerCompanyName ? `, ${session.customerCompanyName}` : ''}
              </div>
            </div>
          )}
          {isAdvisor && (
            <div className={styles.block}>
              <div className={styles.label}>{t('home.advisor')}:</div>
              <div>{session.advisorName}</div>
            </div>
          )}
        </div>
      </ModalHeader>
      <ModalContent>
        {getCompanyInfoQuery.isLoading && (
          <div style={{ height: 200 }}>
            <CenteredSpinner />
          </div>
        )}
        {getCompanyInfoQuery.isFetched &&
          (getCompanyInfoQuery.data?.bookingSessionCredit || 0) === 0 && (
            <div>
              <Trans
                t={t}
                values={{
                  customerName: session.customerFullName,
                  customerCompany: session.customerCompanyName,
                }}
                i18nKey="home.interestForm.advisor.acceptInterestRequest.customerHasNoCoin"
                components={{
                  newline: <br />,
                  phone: (
                    <a className={styles.rescheduleWarningAnchor} href="tel:+46762317714">
                      +46 76 231 77 14
                    </a>
                  ),
                  email: (
                    <a className={styles.rescheduleWarningAnchor} href="mailto:happy@wisory.se">
                      [happy@wisory.se]
                    </a>
                  ),
                }}
              />
            </div>
          )}
        {getCompanyInfoQuery.isFetched &&
          (getCompanyInfoQuery.data?.bookingSessionCredit || 0) > 0 && (
            <Form
              layout="vertical"
              form={form}
              name="dynamic_form_complex"
              autoComplete="off"
              onFinish={submitAction.mutate}
              initialValues={{ purpose: PURPOSE.FOLLOW_UP_PREVIOUS_SESSION }}
            >
              <Form.Item
                label={
                  <Row align="middle">
                    {t('rebuy.bookingForm.dateLabel')}
                    &nbsp;
                    {validateTimeQuery.isFetching && <Spin spinning size="small" />}
                  </Row>
                }
                name="bookingDateAndTime"
                rules={[
                  { required: true, message: t('rebuy.bookingForm.dateRequiredValidation') },
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.resolve();
                      } else {
                        return validateTimeQuery.refetch();
                      }
                    },
                  }),
                ]}
              >
                <DateTimePicker className={styles.datepicker} />
              </Form.Item>
              <Form.Item name="purpose" label={t('rebuy.bookingForm.purposeLabel')} shouldUpdate>
                <Radio.Group onChange={onPurposeChange} className={styles.radioGroupWrapper}>
                  <Radio value={PURPOSE.FOLLOW_UP_PREVIOUS_SESSION}>
                    {t('rebuy.bookingForm.purposeFollowUpOption')}
                  </Radio>
                  <Radio value={PURPOSE.OTHER}>
                    {!showOtherPurpose && t('rebuy.bookingForm.purposeOthersOption')}
                    {showOtherPurpose && (
                      <Form.Item
                        name="otherPurpose"
                        label={t('rebuy.bookingForm.purposeOthersOption')}
                      >
                        <Input />
                      </Form.Item>
                    )}
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Row justify="end" align="middle" gutter={12}>
                    <Col>
                      <Button onClick={onCancel}>{t('form.cancel')}</Button>
                    </Col>
                    <Col>
                      <Button
                        loading={submitAction.isLoading || validateTimeQuery.isFetching}
                        type="primary"
                        htmlType="submit"
                      >
                        {t('rebuy.bookingForm.submitBtn')}
                      </Button>
                    </Col>
                  </Row>
                )}
              </Form.Item>
            </Form>
          )}
      </ModalContent>
    </ModalDialog>
  );
}
